import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Wifi, WifiOff, MapPin, Clock, CheckCircle2, Play,
  AlertTriangle, Filter,
} from 'lucide-react';
import { TechnicianJob, JobStatus, PhotoStage, MaterialLog } from '../../types/technicianJob';
import { technicianService } from '../../services/technician/technicianService';
import { offlineQueue } from '../../services/technician/offlineQueue';
import { TechnicianJobCard } from '../../components/technician/TechnicianJobCard';
import { Badge } from '../../components/ui/Badge';

const DEMO_TECHNICIAN_ID = 'USR-00006';

const STATUS_TABS: { key: JobStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'pending_inspection', label: 'Inspection' },
  { key: 'completed', label: 'Completed' },
];

const STATUS_CARD_MAP: Record<JobStatus, { icon: React.ReactNode; bg: string }> = {
  assigned: { icon: <Clock className="w-4 h-4 text-sky-400" />, bg: 'bg-sky-500/10 border-sky-500/20' },
  accepted: { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, bg: 'bg-emerald-500/10 border-emerald-500/20' },
  in_progress: { icon: <Play className="w-4 h-4 text-amber-400" />, bg: 'bg-amber-500/10 border-amber-500/20' },
  pending_inspection: { icon: <AlertTriangle className="w-4 h-4 text-purple-400" />, bg: 'bg-purple-500/10 border-purple-500/20' },
  completed: { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, bg: 'bg-emerald-500/10 border-emerald-500/20' },
  rejected: { icon: <AlertTriangle className="w-4 h-4 text-rose-400" />, bg: 'bg-rose-500/10 border-rose-500/20' },
};

function isOverdue(slaDeadline: string): boolean {
  return new Date(slaDeadline) < new Date();
}

export const TechnicianWorkspacePage: React.FC = () => {
  const [jobs, setJobs] = useState<TechnicianJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<TechnicianJob | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(search ? { search } : {}),
      };
      const res = await technicianService.getJobs(DEMO_TECHNICIAN_ID, filter);
      if (res.success) setJobs(res.data);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const unsub = offlineQueue.onChange(() => setPendingCount(offlineQueue.getPendingCount()));
    setPendingCount(offlineQueue.getPendingCount());
    return unsub;
  }, []);

  const handleAccept = async (jobId: string) => {
    if (!isOnline) {
      offlineQueue.enqueue(jobId, 'ACCEPT_JOB', { accepted_at: new Date().toISOString() });
      setPendingCount(offlineQueue.getPendingCount());
      return;
    }
    await technicianService.acceptJob({ job_id: jobId, accepted_at: new Date().toISOString() });
    const res = await technicianService.getJobById(jobId);
    if (res.success) {
      setJobs(prev => prev.map(j => j.job_id === jobId ? res.data : j));
      if (selectedJob?.job_id === jobId) setSelectedJob(res.data);
    }
  };

  const handleStart = async (jobId: string) => {
    if (!isOnline) {
      offlineQueue.enqueue(jobId, 'START_JOB', { started_at: new Date().toISOString() });
      setPendingCount(offlineQueue.getPendingCount());
      return;
    }
    await technicianService.startJob({ job_id: jobId, started_at: new Date().toISOString() });
    const res = await technicianService.getJobById(jobId);
    if (res.success) {
      setJobs(prev => prev.map(j => j.job_id === jobId ? res.data : j));
      if (selectedJob?.job_id === jobId) setSelectedJob(res.data);
    }
  };

  const handleAddPhoto = async (jobId: string, photo: { stage: PhotoStage; data_url: string; captured_at: string; notes?: string }) => {
    if (!isOnline) {
      offlineQueue.enqueue(jobId, 'ADD_PHOTO', photo);
      setPendingCount(offlineQueue.getPendingCount());
      return;
    }
    await technicianService.addPhoto({ job_id: jobId, photo });
    const res = await technicianService.getJobById(jobId);
    if (res.success) {
      setJobs(prev => prev.map(j => j.job_id === jobId ? res.data : j));
      if (selectedJob?.job_id === jobId) setSelectedJob(res.data);
    }
  };

  const handleRemovePhoto = async (_jobId: string, _photoId: string) => {
    // Photo removal is local-only in demo; in production this would call adapter
  };

  const handleAddMaterial = async (jobId: string, material: Omit<MaterialLog, 'id' | 'total_cost'>) => {
    if (!isOnline) {
      offlineQueue.enqueue(jobId, 'ADD_MATERIAL', material);
      setPendingCount(offlineQueue.getPendingCount());
      return;
    }
    await technicianService.addMaterial({ job_id: jobId, material });
    const res = await technicianService.getJobById(jobId);
    if (res.success) {
      setJobs(prev => prev.map(j => j.job_id === jobId ? res.data : j));
      if (selectedJob?.job_id === jobId) setSelectedJob(res.data);
    }
  };

  const handleRemoveMaterial = async (_jobId: string, _materialId: string) => {
    // Material removal is local-only in demo
  };

  const handleComplete = async (jobId: string, notes: string, signature?: string) => {
    if (!isOnline) {
      offlineQueue.enqueue(jobId, 'COMPLETE_JOB', { completion_notes: notes, digital_signature: signature, completed_at: new Date().toISOString() });
      setPendingCount(offlineQueue.getPendingCount());
      return;
    }
    await technicianService.completeJob({ job_id: jobId, completion_notes: notes, digital_signature: signature, completed_at: new Date().toISOString() });
    const res = await technicianService.getJobById(jobId);
    if (res.success) {
      setJobs(prev => prev.map(j => j.job_id === jobId ? res.data : j));
      if (selectedJob?.job_id === jobId) setSelectedJob(res.data);
    }
  };

  const filteredJobs = statusFilter === 'all' && !search ? jobs : jobs;

  if (selectedJob) {
    const currentJob = jobs.find(j => j.job_id === selectedJob.job_id) || selectedJob;
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <TechnicianJobCard
          job={currentJob}
          onAccept={handleAccept}
          onStart={handleStart}
          onAddPhoto={handleAddPhoto}
          onRemovePhoto={handleRemovePhoto}
          onAddMaterial={handleAddMaterial}
          onRemoveMaterial={handleRemoveMaterial}
          onComplete={handleComplete}
          onBack={() => setSelectedJob(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Offline Indicator */}
      {!isOnline && (
        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-lg">
          <WifiOff className="w-4 h-4" />
          <span className="text-xs font-semibold">You are offline. Changes will sync when connection is restored.</span>
          {pendingCount > 0 && <Badge label={`${pendingCount} pending`} variant="warning" />}
        </div>
      )}
      {isOnline && pendingCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg">
          <Wifi className="w-4 h-4" />
          <span className="text-xs font-semibold">{pendingCount} offline action(s) pending sync</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search jobs by title, WO ID, location..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:ring-2 focus:ring-sky-500"
          />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map(tab => {
          const count = tab.key === 'all' ? jobs.length : jobs.filter(j => j.status === tab.key).length;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === tab.key
                  ? 'bg-sky-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0 rounded-full ${statusFilter === tab.key ? 'bg-sky-500' : 'bg-slate-700'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Job List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-800 border border-slate-700/60 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-slate-700 rounded w-1/3 mb-2" />
              <div className="h-3 bg-slate-700 rounded w-2/3 mb-3" />
              <div className="flex gap-2">
                <div className="h-6 bg-slate-700 rounded-full w-16" />
                <div className="h-6 bg-slate-700 rounded-full w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700/60 rounded-xl p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400 font-medium">No jobs found</p>
          <p className="text-xs text-slate-500 mt-1">
            {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'No jobs assigned to you yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map(job => {
            const cardConf = STATUS_CARD_MAP[job.status] || STATUS_CARD_MAP.assigned;
            const overdue = isOverdue(job.sla_deadline);
            return (
              <button
                key={job.job_id}
                onClick={() => setSelectedJob(job)}
                className={`w-full text-left bg-slate-800 border border-slate-700/60 rounded-xl p-4 hover:border-sky-500/40 transition-all ${cardConf.bg}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono text-sky-400">{job.work_order_id}</span>
                      {overdue && <Badge label="OVERDUE" variant="danger" />}
                    </div>
                    <p className="text-sm font-semibold text-slate-100 truncate">{job.title}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <MapPin className="w-3 h-3 text-slate-500" />
                      <p className="text-[10px] text-slate-500 truncate">{job.location}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {cardConf.icon}
                    <Badge label={job.priority.toUpperCase()} variant={job.priority === 'critical' || job.priority === 'high' ? 'danger' : job.priority === 'medium' ? 'warning' : 'info'} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
